import {type DataError, DataErrorKind, Either, HttpInterface, UrlUtils} from '@/modern/core';

export type ResponseWithMeta<T> = {
    data: T;
    meta: Meta
}

type Meta = {
    per_page: number
    current_page: number
    total: number
    last_page: number
}

export class BaseRepository {
    static #instance: Record<string, BaseRepository> = {}
    protected httpClient: HttpInterface;
    private internalBaseUrl: UrlUtils;

    constructor(httpClient: HttpInterface) {
        this.httpClient = httpClient;
        this.internalBaseUrl = new UrlUtils(httpClient.baseURL);
    }

    setHeader(name: string, value: string) {
        this.httpClient.setHeader(name, value);
    }

    get baseUrl(): UrlUtils {
        return this.internalBaseUrl;
    }

    static instance<T extends typeof BaseRepository>(
        this: T,
        httpClient: HttpInterface
    ): InstanceType<T> {
        const className = this.name;
        const httpClientClassName = httpClient.clientName;
        const key = `${className}_${httpClientClassName}`;
        if (!BaseRepository.#instance[key]) {
            BaseRepository.#instance[key] = new this(httpClient);
        }
        return BaseRepository.#instance[key] as InstanceType<T>;
    }

    protected async handleSingleRequest<I>(
        request: Promise<any>
    ): Promise<Either<DataError, I>> {
        try {
            const response = this.extractData(await request);
            if (response.error) {
                return Either.left(this.handleError(response));
            }
            const data = response.data as I;
            return Either.right(data);
        } catch (error: any) {
            return Either.left(this.handleError(error));
        }
    }

    protected async handleMultipleRequest<I>(
        request: Promise<any>
    ): Promise<Either<DataError, I[]>> {
        try {
            const response = this.extractData(await request);
            if (response.error) {
                return Either.left(this.handleError(response));
            }
            const data: I[] = response.data as I[];
            return Either.right(data as I[]);
        } catch (error: any) {
            return Either.left(this.handleError(error));
        }
    }

    protected async handleMultipleWithMetaRequest<I>(
        request: Promise<any>,
        processData: (data: I[]) => I[] = (data: I[]) => data
    ): Promise<Either<DataError, ResponseWithMeta<I[]>>> {
        try {
            const response = this.extractData(await request, true);
            if (response.error) {
                return Either.left(this.handleError(response));
            }
            const data: I[] = processData(response.data) as I[];
            const meta: Meta = response.meta as Meta;
            return Either.right({ data, meta });
        } catch (error: any) {
            return Either.left(this.handleError(error));
        }
    }

    protected async handleCustomRequest<T = any>(
        request: Promise<any>,
        processData: (data: any) => any = (data: any) => data
    ): Promise<Either<DataError, T>> {
        try {
            const response = this.extractData(await request);
            if (response.error) {
                return Either.left(this.handleError(response));
            }
            const data = processData(response.data);
            return Either.right(data);
        } catch (error: any) {
            return Either.left(this.handleError(error));
        }
    }

    protected extractData(response: any, withMeta = false): any {
        if (withMeta && response.data.meta) {
            response.meta = response.data.meta
            delete response.data.meta
        }
        if (response.data.data) {
            response.data = response.data.data
        }
        return response
    }

    protected handleError(error: any): DataError {
        if (error.status) {
            if (error.status === 422) {
                return {
                    kind: DataErrorKind.ApiValidationError,
                    message: error.data.errors
                }
            }
            if (error.status === 401) {
                return {
                    kind: DataErrorKind.Unauthorized,
                    message: error.statusText || 'Não autorizado',
                };
            }
            if (error.status === 404) {
                return {
                    kind: DataErrorKind.NotFound,
                    message: error.statusText || 'Recurso não encontrado',
                };
            }
            return {
                kind: DataErrorKind.ApiError,
                message: error.statusText,
                statusCode: error.status
            };
        }
        return {
            kind: DataErrorKind.UnexpectedError,
            message: error,
        }
    }
}
